// src/components/PublicTripPage.jsx
// Public trip view — photos-on-map with Prezi-style camera approach
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../context/UserContext";
import { FRONTEND_URL } from "../constants";

const FL = "'Manrope', -apple-system, sans-serif";
const FH = "'Noto Serif', Georgia, serif";
const TRIP_COLOR = "#E8943A";
const ZOOM_CLOSE = 18;
const PHOTO_SMALL = 56;

function ensureGoogleMaps(cb) {
  if (window.google?.maps?.Map) { cb(); return; }
  if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
    return;
  }
  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=marker&v=weekly&loading=async&language=${localStorage.getItem("lang") || "en"}`;
  script.async = true;
  script.onload = () => {
    if (window.google?.maps?.Map) { cb(); return; }
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
  };
  document.head.appendChild(script);
}

export default function PublicTripPage({ tripId }) {
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preloading, setPreloading] = useState(true);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const polylineRef = useRef(null);
  const overlaysRef = useRef([]);
  const autoRef = useRef(null);
  const prevIdxRef = useRef(-1);
  const goToStopRef = useRef(null);
  const handlePrevRef = useRef(null);
  const handleNextRef = useRef(null);

  const AUTO_INTERVAL = 3000;
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  const titleRef = useRef(null);
  const titleWrapRef = useRef(null);
  const [titleOverflows, setTitleOverflows] = useState(false);

  const PHOTO_ACTIVE_W = isMobile ? Math.min(220, window.innerWidth * 0.55) : 260;

  // Reactive isMobile
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Load trip
  useEffect(() => {
    axios
      .get(`${API_BASE}/trips/${tripId}`)
      .then((res) => {
        const data = res.data;
        const stops = (data.stops || [])
          .filter((s) => s.lat != null && s.lng != null)
          .map((s) => ({
            ...s,
            name: s.name || `Stop`,
            time: s.visited_at || "",
            thumbnailUrl: s.photo_url || (s.photo_urls?.[0]) || null,
            photos: (s.photo_urls || []).map((url) => ({ url })),
          }));
        setTrip({ ...data, stops });
      })
      .catch((err) => {
        if (err.response?.status === 403) setError("This trip is private.");
        else if (err.response?.status === 404) setError("Trip not found.");
        else setError("Failed to load trip.");
      });
  }, [tripId]);

  // Init map
  useEffect(() => {
    if (!trip || !trip.stops?.length) return;
    if (mapRef.current) return;

    ensureGoogleMaps(() => {
      const stops = trip.stops;
      const bounds = new window.google.maps.LatLngBounds();
      stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));

      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: bounds.getCenter(),
        zoom: 14,
        mapId: "trip_public_map",
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "greedy",
      });
      map.fitBounds(bounds, 60);
      mapRef.current = map;

      // Polyline
      polylineRef.current = new window.google.maps.Polyline({
        path: stops.map((s) => ({ lat: s.lat, lng: s.lng })),
        geodesic: true,
        strokeColor: TRIP_COLOR,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map,
      });

      // Create photo overlays on the map
      const overlays = stops.map((stop, i) => {
        const el = document.createElement("div");
        el.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer;";

        const frame = document.createElement("div");
        frame.style.cssText = `
          width:${PHOTO_SMALL}px;height:${PHOTO_SMALL}px;
          border-radius:6px;overflow:hidden;
          border:2px solid rgba(255,255,255,0.9);
          box-shadow:0 2px 8px rgba(0,0,0,0.15);
          position:relative;
          transition: width 0.6s cubic-bezier(0.16,1,0.3,1),
                      height 0.6s cubic-bezier(0.16,1,0.3,1),
                      border-radius 0.4s ease,
                      box-shadow 0.4s ease;
        `;

        const img = document.createElement("img");
        const firstPhoto = stop.photos?.[0];
        img.src = firstPhoto?.url || stop.thumbnailUrl || "";
        img.style.cssText = "width:100%;height:100%;object-fit:cover;display:block;";
        img.draggable = false;
        img.addEventListener("load", () => {
          if (parseInt(frame.style.width) > PHOTO_SMALL && img.naturalWidth > 0) {
            const w = parseInt(frame.style.width);
            const maxH = isMobile ? Math.min(300, window.innerHeight * 0.4) : 350;
            const h = Math.round(w * (img.naturalHeight / img.naturalWidth));
            frame.style.height = Math.max(w * 0.55, Math.min(maxH, h)) + "px";
          }
        });
        frame.appendChild(img);

        if (!firstPhoto?.url && !stop.thumbnailUrl) {
          const numDiv = document.createElement("div");
          numDiv.style.cssText = `
            position:absolute;inset:0;
            display:flex;align-items:center;justify-content:center;
            background:#ede0d5;color:${TRIP_COLOR};
            font-family:Manrope,-apple-system,sans-serif;font-size:18px;font-weight:800;
          `;
          numDiv.textContent = i + 1;
          frame.appendChild(numDiv);
        }

        // Badge
        const badge = document.createElement("div");
        badge.style.cssText = `
          position:absolute;top:6px;right:6px;
          background:rgba(0,0,0,0.5);color:#fff;
          padding:2px 6px;border-radius:4px;
          font-family:Manrope,sans-serif;font-size:10px;font-weight:600;
          backdrop-filter:blur(4px);
          opacity:0;transition:opacity 0.3s;
          pointer-events:none;
        `;
        const photoCount = stop.photos?.length || 0;
        badge.textContent = photoCount > 1 ? `1/${photoCount}` : "";
        frame.appendChild(badge);

        // Dots
        const dots = document.createElement("div");
        dots.style.cssText = `
          position:absolute;bottom:8px;left:50%;
          transform:translateX(-50%);
          display:flex;gap:4px;
          opacity:0;transition:opacity 0.3s;
          pointer-events:none;
        `;
        if (photoCount > 1) {
          for (let j = 0; j < Math.min(photoCount, 8); j++) {
            const dot = document.createElement("span");
            dot.style.cssText = `
              width:5px;height:5px;border-radius:50%;
              background:rgba(255,255,255,${j === 0 ? "1" : "0.5"});
              transition:all 0.3s;
            `;
            dot.dataset.dotIndex = j;
            dots.appendChild(dot);
          }
        }
        frame.appendChild(dots);

        // Swipe gesture on photo
        let touchStartX = 0, touchStartY = 0, swiping = false;
        frame.addEventListener("touchstart", (e) => {
          if (parseInt(frame.style.width) <= PHOTO_SMALL) return;
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          swiping = false;
        }, { passive: true });
        frame.addEventListener("touchmove", (e) => {
          if (parseInt(frame.style.width) <= PHOTO_SMALL) return;
          const dx = e.touches[0].clientX - touchStartX;
          const dy = Math.abs(e.touches[0].clientY - touchStartY);
          if (Math.abs(dx) > dy && Math.abs(dx) > 10) {
            swiping = true;
            e.stopPropagation();
            img.style.transform = `translateX(${dx * 0.3}px)`;
          }
        }, { passive: true });
        frame.addEventListener("touchend", () => {
          if (!swiping) return;
          const dx = parseFloat(img.style.transform.replace(/[^-\d.]/g, "")) || 0;
          img.style.transition = "transform 0.2s ease";
          img.style.transform = "translateX(0)";
          setTimeout(() => { img.style.transition = ""; }, 200);
          if (Math.abs(dx) > 12) {
            if (dx < 0 && handleNextRef.current) handleNextRef.current();
            else if (dx > 0 && handlePrevRef.current) handlePrevRef.current();
          }
          swiping = false;
        });

        el.appendChild(frame);

        // Label
        const label = document.createElement("div");
        label.style.cssText = `
          margin-top:6px;white-space:nowrap;
          font-family:Manrope,-apple-system,sans-serif;
          font-size:11px;font-weight:700;
          color:#2f3430;background:rgba(255,255,255,0.9);
          padding:3px 8px;border-radius:4px;
          box-shadow:0 1px 4px rgba(0,0,0,0.1);
          opacity:0;transition:opacity 0.3s;
          pointer-events:none;max-width:160px;
          overflow:hidden;text-overflow:ellipsis;
        `;
        label.textContent = stop.name || `Stop ${i + 1}`;
        el.appendChild(label);

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat: stop.lat, lng: stop.lng },
          map,
          content: el,
          zIndex: 1,
        });

        marker.addListener("click", () => {
          if (goToStopRef.current) goToStopRef.current(i);
        });

        return { marker, el, frame, img, badge, label, dots };
      });

      overlaysRef.current = overlays;

      // Pre-load tiles
      const waitForIdle = () => new Promise((resolve) => {
        const l = map.addListener("idle", () => { l.remove(); resolve(); });
        setTimeout(() => { l.remove(); resolve(); }, 1500);
      });

      (async () => {
        for (const s of stops) {
          map.moveCamera({ center: { lat: s.lat, lng: s.lng }, zoom: ZOOM_CLOSE });
          await waitForIdle();
          for (const z of [ZOOM_CLOSE - 2, ZOOM_CLOSE - 4]) {
            map.moveCamera({ center: { lat: s.lat, lng: s.lng }, zoom: z });
            await waitForIdle();
          }
        }
        map.fitBounds(bounds, 60);
        await waitForIdle();
        setPreloading(false);
        setFadeIn(true);
        setTimeout(() => {
          if (goToStopRef.current) goToStopRef.current(0);
        }, 300);
      })();
    });
  }, [trip]); // eslint-disable-line

  // Update photo overlay active states
  useEffect(() => {
    const overlays = overlaysRef.current;
    if (!overlays.length) return;
    const stops = trip?.stops || [];

    overlays.forEach((o, i) => {
      const isActive = i === currentIdx && arrived;

      if (isActive) {
        const maxH = isMobile ? Math.min(300, window.innerHeight * 0.4) : 350;
        let h = Math.round(PHOTO_ACTIVE_W * 1.25);
        if (o.img.naturalWidth > 0 && o.img.naturalHeight > 0) {
          h = Math.round(PHOTO_ACTIVE_W * (o.img.naturalHeight / o.img.naturalWidth));
        }
        h = Math.max(Math.round(PHOTO_ACTIVE_W * 0.55), Math.min(maxH, h));

        o.frame.style.width = PHOTO_ACTIVE_W + "px";
        o.frame.style.height = h + "px";
        o.frame.style.borderRadius = "12px";
        o.frame.style.borderWidth = "3px";
        o.frame.style.boxShadow = "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.15)";
        o.badge.style.opacity = (stops[i]?.photos?.length || 0) > 1 ? "1" : "0";
        o.dots.style.opacity = (stops[i]?.photos?.length || 0) > 1 ? "1" : "0";
        o.label.style.opacity = "1";
        o.marker.zIndex = 10;

        const photo = stops[i]?.photos?.[photoIdx];
        if (photo) {
          o.img.src = photo.url;
          o.badge.textContent = `${photoIdx + 1}/${stops[i].photos.length}`;
          const dotEls = o.dots.querySelectorAll("span");
          dotEls.forEach((dot, j) => {
            dot.style.background = j === photoIdx ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.5)";
            dot.style.width = j === photoIdx ? "12px" : "5px";
            dot.style.borderRadius = j === photoIdx ? "3px" : "50%";
          });
        }
      } else {
        o.frame.style.width = PHOTO_SMALL + "px";
        o.frame.style.height = PHOTO_SMALL + "px";
        o.frame.style.borderRadius = "6px";
        o.frame.style.borderWidth = "2px";
        o.frame.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        o.badge.style.opacity = "0";
        o.dots.style.opacity = "0";
        o.label.style.opacity = "0";
        o.marker.zIndex = 1;

        const photo = stops[i]?.photos?.[0];
        if (photo) o.img.src = photo.url;
      }
    });
  }, [currentIdx, arrived, photoIdx, isMobile, trip, PHOTO_ACTIVE_W]);

  // Auto-play
  useEffect(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    if (isPlaying && currentIdx >= 0 && !transitioning) {
      autoRef.current = setInterval(() => {
        const photos = trip?.stops?.[currentIdx]?.photos || [];
        if (photos.length > 1 && photoIdx < photos.length - 1) {
          setPhotoIdx((p) => p + 1);
        } else if (currentIdx < (trip?.stops?.length || 0) - 1) {
          if (goToStopRef.current) goToStopRef.current(currentIdx + 1);
        } else {
          setIsPlaying(false);
        }
      }, AUTO_INTERVAL);
    }
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [isPlaying, currentIdx, transitioning, photoIdx]); // eslint-disable-line

  // Prezi-style flyTo
  const flyTo = useCallback((fromLat, fromLng, fromZoom, toLat, toLng, toZoom, duration = 1500) => {
    return new Promise((resolve) => {
      const map = mapRef.current;
      if (!map) { resolve(); return; }

      const dlat = Math.abs(toLat - fromLat);
      const dlng = Math.abs(toLng - fromLng);
      const dist = Math.sqrt(dlat * dlat + dlng * dlng);
      let zoomDip;
      if (dist < 0.005) zoomDip = 0;
      else if (dist < 0.02) zoomDip = 0.6;
      else if (dist < 0.05) zoomDip = 1.2;
      else if (dist < 0.15) zoomDip = 2.0;
      else if (dist < 0.5) zoomDip = 3.0;
      else zoomDip = 4.0;
      const baseZoom = Math.min(fromZoom, toZoom);

      const start = performance.now();
      duration = Math.round(duration * 1.5);
      const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animate = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        const et = ease(t);

        const lat = fromLat + (toLat - fromLat) * et;
        const lng = fromLng + (toLng - fromLng) * et;

        const zoomOffset = Math.sin(t * Math.PI) * zoomDip;
        const zoom = baseZoom + (toZoom - baseZoom) * et - zoomOffset;

        map.moveCamera({ center: { lat, lng }, zoom });

        if (t < 1) requestAnimationFrame(animate);
        else resolve();
      };
      requestAnimationFrame(animate);
    });
  }, []);

  // Go to stop
  const goToStop = useCallback(async (idx) => {
    const stops = trip?.stops;
    if (!stops || idx < 0 || idx >= stops.length || !mapRef.current || transitioning) return;
    setTransitioning(true);
    setArrived(false);
    setPhotoIdx(0);

    const target = stops[idx];
    const prev = prevIdxRef.current >= 0 ? stops[prevIdxRef.current] : null;
    prevIdxRef.current = idx;

    if (!prev || currentIdx === -1) {
      const center = mapRef.current.getCenter();
      const currentZoom = mapRef.current.getZoom();
      await flyTo(center.lat(), center.lng(), currentZoom, target.lat, target.lng, ZOOM_CLOSE, 1500);
      setCurrentIdx(idx);
      setTransitioning(false);
      setTimeout(() => setArrived(true), 100);
      return;
    }

    const dlat = Math.abs(target.lat - prev.lat);
    const dlng = Math.abs(target.lng - prev.lng);
    const dist = Math.sqrt(dlat * dlat + dlng * dlng);
    const duration = dist < 0.003 ? 1000 : dist < 0.03 ? 1500 : dist < 0.1 ? 2000 : 2500;

    await flyTo(prev.lat, prev.lng, ZOOM_CLOSE, target.lat, target.lng, ZOOM_CLOSE, duration);

    setCurrentIdx(idx);
    setTransitioning(false);
    setTimeout(() => setArrived(true), 100);
  }, [trip, transitioning, currentIdx, flyTo]); // eslint-disable-line

  // Keep goToStopRef current
  useEffect(() => { goToStopRef.current = goToStop; }, [goToStop]);

  const handlePrev = () => {
    if (transitioning) return;
    const photos = trip?.stops?.[currentIdx]?.photos || [];
    if (photoIdx > 0) setPhotoIdx(photoIdx - 1);
    else if (currentIdx > 0) goToStop(currentIdx - 1);
  };

  const handleNext = () => {
    if (transitioning) return;
    const photos = trip?.stops?.[currentIdx]?.photos || [];
    if (photos.length > 1 && photoIdx < photos.length - 1) setPhotoIdx(photoIdx + 1);
    else if (currentIdx < (trip?.stops?.length || 0) - 1) goToStop(currentIdx + 1);
  };

  useEffect(() => { handlePrevRef.current = handlePrev; });
  useEffect(() => { handleNextRef.current = handleNext; });

  const handleShare = () => {
    const url = `${FRONTEND_URL}/trip/${tripId}`;
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check title overflow
  useEffect(() => {
    if (!titleRef.current || !titleWrapRef.current) return;
    const check = () => {
      const textW = titleRef.current.scrollWidth;
      const wrapW = titleWrapRef.current.clientWidth;
      setTitleOverflows(textW > wrapW);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [trip?.name]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); handleNext(); }
      else if (e.key === "p") setIsPlaying((p) => !p);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [currentIdx, photoIdx, transitioning]); // eslint-disable-line

  if (error) {
    return (
      <div style={{
        minHeight: "100dvh", background: "#faf9f6",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", padding: 40,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 56, color: "#8a8e8a", marginBottom: 16 }}>
          error_outline
        </span>
        <p style={{ fontFamily: FH, fontSize: 20, fontStyle: "italic", color: "#2f3430", marginBottom: 8 }}>
          {error}
        </p>
        <a href="/" style={{ fontFamily: FL, fontSize: 14, color: TRIP_COLOR, textDecoration: "none" }}>
          Go to OurCuration
        </a>
      </div>
    );
  }

  if (!trip) {
    return (
      <div style={{
        minHeight: "100dvh", background: "#faf9f6",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <p style={{ fontFamily: FL, fontSize: 14, color: "#8a8e8a" }}>Loading...</p>
      </div>
    );
  }

  const stops = trip.stops;
  const stop = currentIdx >= 0 ? stops[currentIdx] : null;
  const photos = stop?.photos || [];
  const progress = stops.length > 0 ? ((currentIdx + 1) / stops.length) * 100 : 0;
  const isFirst = currentIdx === 0 && photoIdx === 0;
  const isLast = currentIdx === stops.length - 1 && photoIdx >= photos.length - 1;

  const barStyle = isMobile
    ? { position: "absolute", bottom: 24, left: 16, right: 16 }
    : { position: "absolute", bottom: 24, left: 24, width: 380 };

  return (
    <div style={{ width: "100vw", height: "100dvh", position: "relative", overflow: "hidden" }}>
      {/* Full-screen map */}
      <div ref={mapContainerRef} style={{ width: "100%", height: "100%", background: "#e8e8e4" }} />

      {/* Preloading overlay */}
      {preloading && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 70,
          background: "rgba(47,52,48,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 12,
        }}>
          <div style={{
            width: 36, height: 36,
            border: "3px solid rgba(255,255,255,0.3)",
            borderTopColor: TRIP_COLOR,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <p style={{ fontFamily: FL, fontSize: 13, fontWeight: 600, color: "#fff" }}>
            Loading map...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Overlay UI */}
      <div style={{
        position: "absolute", inset: 0,
        pointerEvents: "none",
        opacity: fadeIn ? 1 : 0,
        transition: "opacity 0.5s",
      }}>
        {/* Vignette */}
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.25) 100%)",
          pointerEvents: "none",
        }} />

        {/* Top bar */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "12px 16px 8px",
          background: "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.2), transparent)",
          pointerEvents: "auto",
        }}>
          {/* Title row */}
          <div
            ref={titleWrapRef}
            style={{
              overflow: "hidden",
              marginBottom: 8,
              position: "relative",
              maskImage: titleOverflows ? "linear-gradient(to right, transparent 0%, black 4%, black 90%, transparent 100%)" : "none",
              WebkitMaskImage: titleOverflows ? "linear-gradient(to right, transparent 0%, black 4%, black 90%, transparent 100%)" : "none",
            }}
          >
            <p
              ref={titleRef}
              style={{
                margin: 0, fontFamily: FH, fontStyle: "italic",
                fontSize: 18, fontWeight: 700, color: "#fff",
                textShadow: "0 1px 6px rgba(0,0,0,0.4)",
                whiteSpace: "nowrap",
                display: "inline-block",
                animation: titleOverflows ? "marquee 8s linear infinite" : "none",
                paddingLeft: titleOverflows ? "100%" : 0,
              }}
            >
              {trip.name}
              {trip.date && (
                <span style={{
                  fontFamily: FL, fontStyle: "normal",
                  fontSize: 11, fontWeight: 500,
                  color: "rgba(255,255,255,0.65)",
                  marginLeft: 10,
                }}>
                  {trip.date}
                </span>
              )}
            </p>
          </div>

          {/* Controls row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a href="/" style={{
              background: "rgba(255,255,255,0.2)", border: "none",
              borderRadius: 8, padding: "5px 8px",
              color: "#fff", cursor: "pointer",
              backdropFilter: "blur(8px)",
              display: "flex", alignItems: "center",
              textDecoration: "none", flexShrink: 0,
            }}>
              <span style={{ fontFamily: FH, fontStyle: "italic", fontSize: 12, fontWeight: 700 }}>OurCuration</span>
            </a>
            <div style={{ flex: 1 }} />
            <button
              onClick={handleShare}
              style={{
                background: copied ? "rgba(46,125,50,0.3)" : "rgba(255,255,255,0.2)",
                border: "none", borderRadius: 8, padding: "5px 10px",
                color: "#fff", cursor: "pointer",
                backdropFilter: "blur(8px)",
                display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                {copied ? "check" : "share"}
              </span>
              <span style={{ fontFamily: FL, fontSize: 12, fontWeight: 600 }}>
                {copied ? "Copied!" : "Share"}
              </span>
            </button>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              style={{
                background: "rgba(255,255,255,0.2)", border: "none",
                borderRadius: 8, padding: "5px 10px",
                color: "#fff", cursor: "pointer",
                backdropFilter: "blur(8px)",
                display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                {isPlaying ? "pause" : "play_arrow"}
              </span>
              <span style={{ fontFamily: FL, fontSize: 12, fontWeight: 600 }}>
                {isPlaying ? "Pause" : "Play"}
              </span>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          position: "absolute", top: 72, left: 16, right: 16,
          height: 6, borderRadius: 3,
          background: "rgba(255,255,255,0.2)",
          pointerEvents: "none",
        }}>
          <div style={{
            height: "100%", borderRadius: 3,
            background: TRIP_COLOR,
            width: `${progress}%`,
            transition: "width 0.8s ease",
          }} />
          {stops.map((_, i) => (
            <div key={i} style={{
              position: "absolute",
              left: `${((i + 1) / stops.length) * 100}%`,
              top: -2, transform: "translateX(-50%)",
              width: i === currentIdx ? 8 : 5,
              height: i === currentIdx ? 8 : 5,
              borderRadius: "50%",
              background: i <= currentIdx ? TRIP_COLOR : "rgba(255,255,255,0.5)",
              border: i === currentIdx ? "2px solid #fff" : "none",
              transition: "all 0.3s",
            }} />
          ))}
        </div>

        {/* Bottom bar — compact, place info + nav */}
        {stop && arrived && (
          <div style={{
            ...barStyle,
            pointerEvents: "auto",
            opacity: arrived ? 1 : 0,
            transform: arrived ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(12px)",
            borderRadius: 16,
            padding: "14px 18px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <span style={{
              width: 32, height: 32, borderRadius: "50%",
              background: TRIP_COLOR, color: "#fff",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: FL, fontSize: 14, fontWeight: 800,
              flexShrink: 0,
            }}>{currentIdx + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0, fontFamily: FL, fontSize: 15, fontWeight: 700, color: "#2f3430",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{stop.name || `Stop ${currentIdx + 1}`}</p>
              {stop.time && (
                <p style={{
                  margin: "2px 0 0", fontFamily: FL, fontSize: 12, color: "#8a8e8a",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{stop.time}</p>
              )}
              {stop.memo && (
                <p style={{
                  margin: "3px 0 0", fontFamily: "'Noto Serif', Georgia, serif", fontStyle: "italic", fontSize: 12, color: "#5c605c",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>"{stop.memo}"</p>
              )}
            </div>
            <span style={{
              fontFamily: FL, fontSize: 10, color: "#8a8e8a",
              background: "#f4f4f0", padding: "3px 8px", borderRadius: 4,
              flexShrink: 0,
            }}>
              {currentIdx + 1} / {stops.length}
            </span>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button onClick={handlePrev} disabled={isFirst} style={{
                width: 40, height: 40, borderRadius: 8,
                border: "none", cursor: isFirst ? "default" : "pointer",
                background: isFirst ? "#f4f4f0" : "#655d54",
                color: isFirst ? "#8a8e8a" : "#fff6ef",
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: isFirst ? 0.4 : 1,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
              </button>
              <button onClick={handleNext} disabled={isLast} style={{
                width: 40, height: 40, borderRadius: 8,
                border: "none", cursor: isLast ? "default" : "pointer",
                background: isLast ? "#f4f4f0" : TRIP_COLOR,
                color: isLast ? "#8a8e8a" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: isLast ? 0.4 : 1,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>
          </div>
        )}

        {/* Transition label */}
        {transitioning && currentIdx >= 0 && (
          <div style={{
            position: "absolute",
            bottom: isMobile ? 80 : 40,
            left: "50%", transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.5)", color: "#fff",
            padding: "6px 14px", borderRadius: 8,
            fontFamily: FL, fontSize: 12, fontWeight: 600,
            backdropFilter: "blur(8px)",
            pointerEvents: "none",
            animation: "fadeInOut 1.8s ease",
          }}>
          {stops[currentIdx]?.name || `Stop ${currentIdx + 1}`}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <a
        href="/"
        style={{
          position: "absolute", bottom: 8, right: 8,
          background: "rgba(255,255,255,0.85)", backdropFilter: "blur(8px)",
          padding: "5px 10px", borderRadius: 6,
          fontFamily: FL, fontSize: 10, fontWeight: 600,
          color: TRIP_COLOR, textDecoration: "none",
          zIndex: 10, pointerEvents: "auto",
        }}
      >
        ourcuration.com
      </a>

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(8px); }
          20% { opacity: 1; transform: translateX(-50%) translateY(0); }
          80% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
}
